import React, { useEffect, useRef, useState } from 'react';
import { Button, Empty, Skeleton, Tooltip } from 'antd';
import { DeleteOutlined, EyeInvisibleOutlined, EyeOutlined, IdcardOutlined, RightOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';
import dayjs from 'dayjs';
import ConciergeBellIcon from '../../receptionist_ai/components/ConciergeBellIcon';
import { ContactFollowedBy } from '../../contacts/components/ContactTags';
import { ConversationCallItem, ConversationSmsItem } from '../../../../domain/models/Conversation';
import { ConversationDisplay, formatCallDuration, formatThreadDay } from '../types/ConversationDisplay';
import useConversationThreadController from '../controllers/useConversationThreadController';
import ConversationComposer from './ConversationComposer';
import CallDirectionIcon, { CALL_LABELS, getCallDirection } from './CallDirectionIcon';
import ConversationCallDrawer from './ConversationCallDrawer';

const SOON_LABEL = 'Bientôt disponible';

interface ConversationThreadProps {
  conversation: ConversationDisplay;
  isContactOpen: boolean;
  onToggleContact: () => void;
  // Opens the full contact sheet
  onOpenContact: () => void;
  onSetRead: (read: boolean) => void;
}

const SmsBubble: React.FC<{ item: ConversationSmsItem; contactName: string }> = ({ item, contactName }) => {
  const isReceived = item.role === 'user';
  return (
    <div className={classNames('conversations__sms', { 'conversations__sms--sent': !isReceived })}>
      <div className="conversations__sms-label">
        {isReceived ? contactName : 'Vous'} · SMS · {dayjs(item.timestamp).format('HH:mm')}
      </div>
      <div className="conversations__sms-bubble">{item.message}</div>
    </div>
  );
};

// A body cut to two lines, then a footer opening the call detail
const CallCard: React.FC<{ item: ConversationCallItem; onOpen: () => void }> = ({ item, onOpen }) => {
  const direction = getCallDirection(item);
  // A missed call without a summary still says why the card is empty
  const isEmptyMissed = direction === 'missed' && !item.summary && !item.is_receptionist;

  return (
    <div
      className={classNames('conversations__call', {
        'conversations__call--outbound': item.direction === 'outbound',
        'conversations__call--missed': direction === 'missed',
      })}
    >
      <div className="conversations__call-body">
        <div className="conversations__call-head">
          {item.is_receptionist ? (
            <span className="conversations__receptionist-badge">
              <ConciergeBellIcon />
              Réceptionniste Donna
            </span>
          ) : (
            <span className="conversations__call-label">
              <CallDirectionIcon direction={direction} />
              {CALL_LABELS[direction]}
            </span>
          )}
          <span className="conversations__call-duration">{formatCallDuration(item.duration)}</span>
          <span className="conversations__call-time">{dayjs(item.timestamp).format('HH:mm')}</span>
        </div>
        {(item.title || isEmptyMissed) && (
          <div className="conversations__call-title">{item.title ?? 'Appel sans réponse'}</div>
        )}
        {item.summary && (
          <div className="conversations__call-summary">
            <ReactMarkdown>{item.summary}</ReactMarkdown>
          </div>
        )}
        {isEmptyMissed && <div className="conversations__call-summary">Aucun message ni enregistrement associé.</div>}
      </div>
      <button type="button" className="conversations__call-footer" onClick={onOpen}>
        {item.summary ? 'Voir résumé' : 'Voir le détail'}
        <RightOutlined />
      </button>
    </div>
  );
};

const ConversationThread: React.FC<ConversationThreadProps> = ({ conversation, isContactOpen, onToggleContact, onOpenContact, onSetRead }) => {
  const { state } = useConversationThreadController(conversation.subscriptionId, conversation.phone);
  const messagesRef = useRef<HTMLDivElement>(null);
  const [openedCall, setOpenedCall] = useState<ConversationCallItem | null>(null);

  // Opens on the most recent exchange, at the bottom
  useEffect(() => {
    const messages = messagesRef.current;
    if (messages) messages.scrollTop = messages.scrollHeight;
  }, [state.days]);

  const isUnread = conversation.unreadCount > 0;

  return (
    <div className="conversations__thread">
      <div className="conversations__thread-header">
        <span className="contacts__avatar contacts__avatar--large">{conversation.initials}</span>
        <div className="conversations__thread-identity">
          <div className="conversations__thread-name">{conversation.fullName}</div>
          <div className="conversations__thread-subtitle">
            {conversation.contact && <span>{conversation.formattedPhone}</span>}
            <ContactFollowedBy label={conversation.followedBy} isOwn={conversation.isOwn} />
          </div>
        </div>
        <div className="conversations__thread-actions">
          <Button
            icon={<IdcardOutlined />}
            type={isContactOpen ? 'primary' : 'default'}
            ghost={isContactOpen}
            aria-expanded={isContactOpen}
            onClick={onToggleContact}
          >
            Détails
          </Button>
          {/* The read state belongs to the owner of the line */}
          {conversation.isOwn && (
            <Button icon={isUnread ? <EyeOutlined /> : <EyeInvisibleOutlined />} onClick={() => onSetRead(isUnread)}>
              {isUnread ? 'Marquer comme lu' : 'Marquer comme non lu'}
            </Button>
          )}
          <Tooltip title={SOON_LABEL}>
            <Button icon={<DeleteOutlined />} disabled aria-label="Supprimer" />
          </Tooltip>
        </div>
      </div>

      <div className="conversations__messages" ref={messagesRef}>
        {state.isLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : state.days.length === 0 ? (
          <Empty description={state.hasFailed ? 'Conversation indisponible' : 'Aucun échange'} />
        ) : (
          state.days.map((day) => (
            <div key={day.timestamp} className="conversations__day">
              <div className="conversations__day-separator">
                <span>{formatThreadDay(day.timestamp)}</span>
              </div>
              {day.items.map((item) =>
                item.kind === 'sms' ? (
                  <SmsBubble key={item.id} item={item} contactName={conversation.fullName} />
                ) : (
                  <CallCard key={item.id} item={item} onOpen={() => setOpenedCall(item)} />
                ),
              )}
            </div>
          ))
        )}
      </div>

      <ConversationComposer key={conversation.key} isOwn={conversation.isOwn} ownerLabel={conversation.followedBy} />

      <ConversationCallDrawer
        conversation={conversation}
        item={openedCall}
        onClose={() => setOpenedCall(null)}
        onShowDetails={() => {
          setOpenedCall(null);
          onOpenContact();
        }}
      />
    </div>
  );
};

export default ConversationThread;
